import {
  AvatarUpload,
  Box,
  Button,
  Flex,
  Form,
  FormContext,
  Spinner,
  toast,
  useScreenResolution,
  useServerResponseTranslation,
  ViewerContext,
} from "dash-ui";
import {
  GetCurrentBrokerDocument,
  useGetBrokerForAccountManagementQuery,
  useUpdateCurrentBrokerMutation,
  useUploadDraftFileMutation,
} from "gql/generated.graphql";
import { FormEvent, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { FormFields } from "./AccountInfoForm.constants";
import {
  compareFormData,
  getExistedWorkingHours,
} from "./AccountInfoForm.helpers";
import MainBrokerInfo from "./components/MainBrokerInfo/MainBrokerInfo";
import WorkingHoursInfo from "./components/WorkingHoursInfo/WorkingHoursInfo";
import BrokerDescriptionInfo from "./components/BrokerDescriptionInfo/BrokerDescriptionInfo";
import { useValidation } from "./AccountInfoForm.hooks";
import { capitalize } from "lodash";

const validatableFields = Object.values(FormFields).filter(
  (field) =>
    ![
      FormFields.PhoneNumberStatus,
      FormFields.PhoneNumberVerificationCode,
      FormFields.Place,
    ].includes(field as FormFields)
) as FormFields[];

const AccountInfoForm = () => {
  const { t } = useTranslation("common");
  const formRef = useRef<HTMLFormElement | null>(null);
  const {
    formState,
    formOnSubmit,
    stateHandlers,
    setFormValues,
    resetFormErrors,
  } = useContext(FormContext);
  const { setCanRedirect, setProfile } = useContext(ViewerContext);
  const translateErrorMessage = useServerResponseTranslation();
  const [uploadDraftFileMutation, { loading: isFileUploading }] =
    useUploadDraftFileMutation();
  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();
  const { data, loading } = useGetBrokerForAccountManagementQuery({
    fetchPolicy: "network-only",
  });
  const broker = data?.getBrokerForAccountManagement;
  const [updateBrokerMutation, { loading: updateBrokerLoading }] =
    useUpdateCurrentBrokerMutation({
      refetchQueries: [GetCurrentBrokerDocument],
    });
  const validate = useValidation();

  const buttonLoading = isFileUploading || updateBrokerLoading;

  const initialFormValues = useMemo(() => {
    if (!broker) return formState;
    const { account, profile, license, ...restBroker } = broker;

    return {
      [FormFields.Avatar]: profile.avatarFile,
      [FormFields.FirstName]: profile.firstName,
      [FormFields.LastName]: profile.lastName,
      [FormFields.PhoneNumber]: account.phone?.number,
      [FormFields.AgencyName]: restBroker.agencyName,
      [FormFields.AgencyWebsite]: restBroker.agencyWebsite,
      [FormFields.PhoneNumberDialCode]: account.phone?.dialCode,
      [FormFields.ShortDescription]: restBroker.description,
      [FormFields.BrokerLicenseNumber]: license?.licenseNumber,
      [FormFields.WorkingHours]: restBroker.workingHours,
      [FormFields.PhoneNumberStatus]: account.phone?.status,
      [FormFields.State]: license?.licenseJurisdictionCode,
      [FormFields.BrokerLicenseStatus]: license?.licenseStatus,
    };
  }, [broker, formState]);

  const setInitialValues = () => setFormValues(initialFormValues);

  useEffect(() => {
    setInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broker]);

  const onFileChange = (file: File | null) => {
    if (file) {
      uploadDraftFileMutation({ variables: { input: { file } } })
        .then(({ data }) => {
          if (data) {
            stateHandlers[FormFields.Avatar](data.uploadDraftFile);
          }
        })
        .catch((error) => toast.error(translateErrorMessage(error)));
    }
  };

  const isDataEqual = useMemo(
    () => compareFormData(formState, initialFormValues),
    [formState, initialFormValues]
  );

  const updateBroker = (isResend?: boolean) => {
    const { __typename, ...workingHours } = getExistedWorkingHours(
      formState.workingHours
    ) as any;

    updateBrokerMutation({
      variables: {
        input: {
          agencyName: formState.agencyName,
          agencyWebsite: formState.agencyWebsite,
          description: formState.shortDescription,
          workingHours,
          license: {
            licenseNumber: formState.brokerLicenseNumber,
            licenseJurisdictionCode: formState.state,
          },
          profileInput: {
            firstName: capitalize(formState.firstName),
            lastName: capitalize(formState.lastName),
            avatarFileId: formState.avatar?._id,
          },
          accountInput: {
            phone: {
              number: formState.phoneNumber,
              dialCode: formState.phoneNumberDialCode,
            },
          },
        },
      },
      onError: (error) => toast.error(translateErrorMessage(error)),
      onCompleted: (data) => {
        if (setCanRedirect) {
          setCanRedirect(false);
        }
        resetFormErrors();
        toast.success(t("pages:profilePage.toasts.updatedProfile"));
        if (setProfile) {
          setProfile(data.updateCurrentBroker.profile);
        }
      },
    });
  };

  const submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    formOnSubmit();

    if (
      isDataEqual ||
      validatableFields.some((field) => !validate(field, formState[field]))
    )
      return;

    updateBroker();
  };

  return (
    <Form id="account-info-form" className="account-info-form" w="100%" h="100%" ref={formRef} onSubmit={submitForm} pos="relative">
      {loading ? (
        <div className="h-100 flex align-center justify-center mt-6">
          <Spinner fullContent />
        </div>
      ) : (
        <>
          <Flex w="100%" flexDirection={isMobile ? "column" : "row"} mt={3}>
            <AvatarUpload
              isDeletable
              onDelete={() => stateHandlers[FormFields.Avatar](null)}
              url={formState.avatar?.publicUrl ?? ""}
              isLoading={isFileUploading}
              onChange={onFileChange}
              {...(!isMobile ? { large: true } : { medium: true })}
            />
            <MainBrokerInfo broker={broker} />
          </Flex>
          <Box maxWidth={["100%", "100%", "100%", "100%"]} ml={[0, 0, 0, 0]}>
            <BrokerDescriptionInfo />
            <WorkingHoursInfo />
            <Flex mt={6}>
              <Button
                type="button"
                px={9}
                mr={2}
                onClick={() => {
                  if (formRef.current) {
                    formRef.current.requestSubmit();
                  }
                }}
                disabled={isDataEqual}
                isLoading={buttonLoading}
              >
                {t("labels.save")}
              </Button>
              <Button
                type="button"
                px={7}
                variation="outline"
                onClick={setInitialValues}
              >
                {t("labels.cancel")}
              </Button>
            </Flex>
          </Box>
        </>
      )}
    </Form>
  );
};

export default AccountInfoForm;
